import React, { useEffect, useState } from "react";
import { Loader2, AlertTriangle, TrendingUp } from "lucide-react";
import { csuiteApi } from "../../../lib/csuiteApi";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const money = (n) => n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K` : `$${n}`;

/** Value Realization Tracker — planned vs achieved benefits, per initiative. */
export default function ValueRealization() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { csuiteApi.value().then(setD).catch(e => setErr(e.message)); }, []);
  if (err) return <div className="p-6 text-red-700 flex items-center gap-2"><AlertTriangle size={16}/>{err}</div>;
  if (!d) return <div className="p-10 grid place-items-center"><Loader2 className="animate-spin text-sky-700"/></div>;

  const chartData = d.benefits.map(b => ({ name: b.benefit.split(" ").slice(0, 3).join(" "), Planned: b.planned, Achieved: b.achieved }));
  const pctRealised = d.totals.planned > 0 ? Math.round((d.totals.achieved / d.totals.planned) * 100) : 0;

  return (
    <div data-testid="platinum-value">
      <div className="mb-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Value Realization</div>
        <h2 className="font-display text-2xl font-extrabold tracking-tight">Planned vs Achieved Benefits</h2>
      </div>

      <div className="grid md:grid-cols-4 gap-3 mb-4">
        <T label="Planned"   value={money(d.totals.planned)}  tone="neutral" />
        <T label="Achieved"  value={money(d.totals.achieved)} tone="green"  sub={`${pctRealised}% realised`} />
        <T label="On Track"  value={money(d.totals.onTrack)}  tone="blue" />
        <T label="At Risk"   value={money(d.totals.atRisk)}   tone="red" />
      </div>

      <div className="card p-4 mb-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Planned vs Achieved by initiative</div>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer><BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${v/1_000_000}M`} />
            <Tooltip formatter={v => money(v)} contentStyle={{ fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="Planned"  fill="#94A3B8" radius={[3,3,0,0]} />
            <Bar dataKey="Achieved" fill="#0369A1" radius={[3,3,0,0]} />
          </BarChart></ResponsiveContainer>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-[var(--border)]">
            <tr>
              <th className="text-left px-3 py-2">Benefit</th>
              <th className="text-left px-3 py-2">Owner</th>
              <th className="text-right px-3 py-2">Planned</th>
              <th className="text-right px-3 py-2">Achieved</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Quarter</th>
            </tr>
          </thead>
          <tbody>
            {d.benefits.map((b, i) => (
              <tr key={b.id} className="border-b border-[var(--border)] last:border-0" data-testid={`value-row-${i}`}>
                <td className="px-3 py-2 font-medium">{b.benefit}</td>
                <td className="px-3 py-2 text-xs">{b.owner}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{money(b.planned)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{money(b.achieved)}</td>
                <td className="px-3 py-2">
                  <span className={`pill text-[10px] ${b.status === "Exceeding" ? "pill-green" : b.status === "On Track" ? "pill-blue" : b.status === "At Risk" ? "pill-amber" : "pill-red"}`}>
                    {b.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">{b.quarter}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function T({ label, value, sub, tone }) {
  const cls = tone === "green" ? "text-emerald-700" : tone === "blue" ? "text-sky-700" : tone === "red" ? "text-red-700" : "text-zinc-900";
  return (
    <div className="card p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display text-2xl font-extrabold mt-1 ${cls}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}
